/** biome-ignore-all lint/a11y/noStaticElementInteractions: some divs need to be interactive */
import { useDraggable } from '@dnd-kit/core';
import type { TitleSize } from '@tempo-io/roadmap-dto';
import { Avatar, AvatarFallback, AvatarImage } from '@tempo-io/sui-avatar';
import { Badge } from '@tempo-io/sui-badge';
import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { KeyboardArrowDown, KeyboardArrowRight } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { addDays, differenceInDays, format, isBefore } from 'date-fns';
import {
  type Dispatch,
  forwardRef,
  memo,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Resizable, type ResizeCallbackData } from 'react-resizable';
import { ItemStatusBadge } from '@/components/ui/itemStatusBadge';
import { getItemTitleFontSize } from '@/components/ui/swimlane/layout/body/cards/CardUtils';
import { useGetItemStatusByIdQuery } from '@/lib/queries/common/useGetItemStatusesQuery';
import { useGetTeamQuery } from '@/lib/queries/teams/useGetTeamQuery';
import { useGetEstimationsByIds } from '@/lib/queries/work-integrations/useGetEstimations';
import { DateFormat } from '@/lib/utils/common/dateFormats';
import { getContrastTextColor } from '@/lib/utils/loop-visualization/colors';
import { progressFieldIdToProgress, systemProgressToProgress } from '../utils/itemProgressUtils';
import { useViewContext } from '../ViewContext';
import { useDependencyContext } from './dependencies/DependencyContext';
import { useItemPositionTracking } from './dependencies/useItemPositionTracking';
import type { TimelineItemType, TimelineRowType } from './useTimelineTableColumns';
import { getProgressUnfilledColor, lightenTimelineBgColor } from './utils/timelineColorUtils';
import { getNormalizePlannedDates } from './utils/timelineDateUtils';
import { stackTimelineItems } from './utils/timelineStackingUtils';

const MIN_ITEM_WIDTH = 20;
export const ITEM_LANE_HEIGHT = 60;
export const ITEM_LANE_HEIGHT_COMPACT = 30;
export const ITEM_HEIGHT = 24;
export const ITEM_HEIGHT_COMPACT = 26;
const ITEM_TOOLTIP_DELAY = 600;
const STALE_TIME_MS = 60 * 60 * 1000; // 1 hour

const ResizeHandle = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    handleAxis?: string;
    itemTopOffset?: number;
    itemHeight?: number;
    isCompact?: boolean;
  }
>(
  (
    { handleAxis, itemTopOffset = 0, itemHeight = ITEM_HEIGHT, isCompact = false, ...props },
    ref,
  ) => {
    const handleHeight = isCompact ? itemHeight - 4 : Math.min(16, itemHeight);
    const handleTopOffset = itemTopOffset + (itemHeight - handleHeight) / 2;

    return (
      <div
        ref={ref}
        data-no-dnd="true"
        className={`react-resizable-handle react-resizable-handle-${handleAxis} absolute px-0.5 cursor-ew-resize z-10 opacity-0 group-hover:opacity-100 bg-bg-warning-subtle hover:bg-bg-surface-base w-1 rounded-lg`}
        style={{
          touchAction: 'none',
          top: handleTopOffset,
          height: handleHeight,
          ...(handleAxis === 'w' ? { left: 6 } : handleAxis === 'e' ? { right: 6 } : {}),
        }}
        {...props}
      />
    );
  },
);

const TimelineItem = memo(
  ({
    item,
    color,
    isCompact,
    calculatedWidth,
    itemTopOffset,
    itemHeight,
    progress,
    fontSize,
    timelineStartDate,
    timelineEndDate,
    startDateToUse,
    endDateToUse,
    isExpanded,
    hasChildren,
    onExpandToggle,
    canResize,
    canDragDrop,
    isChild,
    onItemClick,
    dayWidth,
    onResizeEnd,
    row,
  }: {
    item: TimelineItemType;
    color: string;
    isCompact: boolean;
    calculatedWidth: number;
    itemTopOffset: number;
    itemHeight: number;
    progress: number | null;
    fontSize: number;
    timelineStartDate: string;
    timelineEndDate: string;
    startDateToUse: string;
    endDateToUse: string;
    isExpanded?: boolean;
    hasChildren?: boolean;
    canResize: boolean;
    canDragDrop: boolean;
    isChild: boolean;
    onItemClick: (itemId: number) => void;
    dayWidth: number;
    onExpandToggle?: (e: React.MouseEvent) => void;
    onResizeEnd?: (itemId: number, newStartDate: string, newEndDate: string) => void;
    row?: TimelineRowType;
  }) => {
    const { t } = useTranslation();
    const hasProgress = progress !== null;
    const barBgColor = hasProgress ? getProgressUnfilledColor(color) : color;
    const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [isDelayedHover, setIsDelayedHover] = useState(false);
    const itemRef = useRef<HTMLDivElement>(null);
    const initialWidthRef = useRef<number>(0);
    const [resizeState, setResizeState] = useState<{
      width: number;
      rightOffset: number;
      handle: 'w' | 'e';
      startDate?: string;
      endDate?: string;
    } | null>(null);

    const { isPublishedMode } = useViewContext();
    const { setIsResizing } = useDependencyContext();

    const startDateToUseForDrag = item.plannedStartDateTime;
    const endDateToUseForDrag = item.plannedFinishDateTime;
    const duration =
      startDateToUseForDrag && endDateToUseForDrag
        ? differenceInDays(new Date(endDateToUseForDrag), new Date(startDateToUseForDrag)) + 1
        : 1;

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
      id: `timeline-item-${item.id}`,
      data: {
        type: 'timeline-item',
        itemId: item.id,
        startDate: startDateToUseForDrag,
        endDate: item.plannedFinishDateTime,
        duration,
        currentRow: row,
        order: item.order ?? 0,
        color,
        name: item.name,
        isCompact,
      },
      disabled: !canDragDrop || !!resizeState || isChild,
    });

    const isTooltipOpen = !!resizeState || isDelayedHover;

    const { data: teamData, isLoading: isTeamLoading } = useGetTeamQuery({
      teamId: item.teamId ?? undefined,
      options: {
        staleTime: STALE_TIME_MS,
        enabled: !!item.teamId && isTooltipOpen && !isPublishedMode,
      },
    });
    const { data: estimationsByIds, isLoading: isEstimationSchemeLoading } = useGetEstimationsByIds(
      {
        estimationIds: new Set(item?.estimationId ? [item.estimationId] : []),
        enabled: !!item.estimationId && isTooltipOpen && !isPublishedMode,
        staleTime: STALE_TIME_MS,
      },
    );
    const { data: itemStatuses, isLoading: isItemStatusesLoading } = useGetItemStatusByIdQuery({
      id: item.itemStatusId,
      options: {
        enabled: isTooltipOpen && !!item.itemStatusId && !isPublishedMode,
        staleTime: STALE_TIME_MS,
      },
    });

    const handleMouseEnter = () => {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsDelayedHover(true);
      }, ITEM_TOOLTIP_DELAY);
    };

    const handleMouseLeave = () => {
      setIsDelayedHover(false);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
    };

    useEffect(() => {
      return () => {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
        }
      };
    }, []);

    const resizedWidth = resizeState ? resizeState.width : calculatedWidth;

    const onResizeStart = (_e: React.SyntheticEvent, { size, handle }: ResizeCallbackData) => {
      initialWidthRef.current = calculatedWidth;
      setIsResizing(true);
      setResizeState({
        width: size.width,
        rightOffset: 0,
        handle: handle as 'w' | 'e',
        startDate: item.plannedStartDateTime || undefined,
        endDate: item.plannedFinishDateTime || undefined,
      });
    };

    const onResize = (_e: React.SyntheticEvent, { handle, size }: ResizeCallbackData) => {
      const widthChange = size.width - initialWidthRef.current;
      const daysChange = Math.round(widthChange / dayWidth);
      if (handle === 'w') {
        const newStartDate = addDays(startDateToUse, -daysChange);
        const rightOffset = initialWidthRef.current - size.width;

        setResizeState((prev) => ({
          ...(prev ? prev : {}),
          width: size.width,
          rightOffset,
          handle: handle as 'w' | 'e',
          startDate: format(newStartDate, 'yyyy-MM-dd'),
        }));
      } else if (handle === 'e') {
        const newEndDate = addDays(endDateToUse, daysChange);

        setResizeState((prev) => ({
          ...(prev ? prev : {}),
          width: size.width,
          rightOffset: 0,
          handle: handle as 'w' | 'e',
          endDate: format(newEndDate, 'yyyy-MM-dd'),
        }));
      }
    };

    const onResizeStop = (_e: React.SyntheticEvent, { size, handle }: ResizeCallbackData) => {
      const widthChange = size.width - initialWidthRef.current;
      const daysChange = Math.round(widthChange / dayWidth);

      let newStartDate = startDateToUse;
      let newEndDate = endDateToUse;

      if (handle === 'w') {
        newStartDate = format(addDays(new Date(startDateToUse), -daysChange), 'yyyy-MM-dd');
      } else if (handle === 'e') {
        newEndDate = format(addDays(new Date(endDateToUse), daysChange), 'yyyy-MM-dd');
      }
      if (onResizeEnd) {
        onResizeEnd(item.id, newStartDate, newEndDate);
      }

      initialWidthRef.current = 0;
      setIsResizing(false);

      setResizeState(null);
    };

    const tooltipStartDate = resizeState?.startDate || item.plannedStartDateTime;
    const tooltipEndDate = resizeState?.endDate || item.plannedFinishDateTime;

    const displayStartDate = tooltipStartDate || item.plannedStartDateTime;
    const displayEndDate = tooltipEndDate || item.plannedFinishDateTime;

    const disableResize = !canResize || isChild;
    return (
      <div
        className={cn('absolute group z-20 rounded-lg', {
          'bg-bg-subtle!': !isChild && isCompact && !isDragging,
          'bg-bg-surface-base': !isChild && !isCompact && !isDragging,
        })}
        style={{
          top: itemTopOffset,
          left: resizeState ? resizeState.rightOffset : 0,
          width: resizedWidth,
          height: itemHeight,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Resizable
          width={resizedWidth}
          height={itemHeight}
          resizeHandles={disableResize ? [] : ['w', 'e']}
          handle={<ResizeHandle itemTopOffset={0} itemHeight={itemHeight} isCompact={isCompact} />}
          onResizeStart={onResizeStart}
          onResize={onResize}
          onResizeStop={onResizeStop}
        >
          <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <Tooltip open={isTooltipOpen}>
              <TooltipTrigger>
                {/** biome-ignore lint/a11y/useKeyWithClickEvents: needs listeners */}
                <div
                  ref={(node) => {
                    itemRef.current = node;
                    setNodeRef(node);
                  }}
                  className={cn('w-[calc(100%-16px)] left-0 overflow-hidden', {
                    'rounded-lg': !isCompact,
                    'rounded-sm': isCompact,
                    'rounded-l-none': startDateToUse === timelineStartDate,
                    'rounded-r-none': endDateToUse === timelineEndDate,
                    group: isChild || !canResize,
                  })}
                  style={{
                    backgroundColor: barBgColor,
                    height: itemHeight,
                    width: resizedWidth,
                    opacity: isDragging ? 0 : 1,
                    cursor: isDragging
                      ? 'grabbing'
                      : resizeState
                        ? 'ew-resize'
                        : disableResize
                          ? 'pointer'
                          : 'grab',
                  }}
                  onClick={() => {
                    onItemClick(item.id);
                  }}
                  {...(!isChild ? attributes : {})}
                  {...(!isChild ? listeners : {})}
                >
                  {hasProgress && (
                    <div
                      className={cn('h-full', {
                        'rounded-lg': !isCompact,
                        'rounded-sm': isCompact,
                        'rounded-l-none': startDateToUse === timelineStartDate,
                        'rounded-r-none': endDateToUse === timelineEndDate,
                      })}
                      style={{
                        backgroundColor: color,
                        width: `${progress}%`,
                      }}
                    />
                  )}
                  <div
                    className={cn(
                      'absolute inset-0 flex items-center justify-center z-5 overflow-hidden px-1',
                      {
                        'pl-2': resizedWidth > 40,
                      },
                    )}
                  >
                    <span
                      className="pl-1 truncate font-semibold"
                      style={{
                        color: getContrastTextColor(color),
                        fontSize,
                      }}
                    >
                      {item.name}
                    </span>
                    {hasProgress && resizedWidth > 60 && (
                      <span
                        className="pl-1 shrink-0 font-normal opacity-90"
                        style={{
                          color: getContrastTextColor(color),
                          fontSize,
                        }}
                      >
                        {t(($) => $.loopVisualization.timeline.progressPercentage, {
                          value: progress,
                        })}
                      </span>
                    )}

                    {hasChildren && onExpandToggle && (
                      <Button
                        asChild
                        variant="ghost"
                        size="icon-sm"
                        style={{ color: getContrastTextColor(color) }}
                        className="rounded-full bg-bg-info-subtle/40 ml-auto mr-2.5 max-h-max max-w-max hover:bg-bg-info-subtle/60"
                        onClick={onExpandToggle}
                      >
                        {isExpanded ? (
                          <KeyboardArrowDown className="h-5 w-5" />
                        ) : (
                          <KeyboardArrowRight className="h-5 w-5" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent
                align="start"
                side="bottom"
                className="bg-bg-surface-base text-text-neutral-primary border-2 min-w-64 w-max"
                style={{
                  left: resizedWidth / 4,
                }}
              >
                <div className="flex flex-col gap-2 p-2 w-full">
                  <span className="font-bold text-base mb-2 w-full">{item.name}</span>

                  <div className="border-b-2 text-text-neutral-secondary" />

                  <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-2 items-center">
                    {!isTeamLoading && teamData && (
                      <>
                        <strong>{t(($) => $.loopVisualization.layout.headers.fields.team)}:</strong>
                        <Badge variant="secondary">
                          <Avatar className="h-4 w-4">
                            <AvatarImage src={teamData.team.icon} />
                            <AvatarFallback className="bg-bg-neutral">
                              {teamData.team.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          {teamData.team.name}
                        </Badge>
                      </>
                    )}
                    {!isEstimationSchemeLoading && estimationsByIds && item.estimationId && (
                      <>
                        <strong>{t(($) => $.common.filters.labels.items.effortEstimate)}:</strong>
                        <Badge variant="secondary">
                          {estimationsByIds[item.estimationId]?.label}
                        </Badge>
                      </>
                    )}
                    {!isItemStatusesLoading && itemStatuses && (
                      <>
                        <strong>
                          {t(($) => $.loopVisualization.layout.headers.fields.status)}:
                        </strong>
                        <ItemStatusBadge label={itemStatuses.name} color={itemStatuses.color} />
                      </>
                    )}
                  </div>

                  <div className="border-b-2 text-text-neutral-secondary" />

                  <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-2 items-center">
                    <strong>
                      {t(($) => $.loopVisualization.layout.headers.fields.startDate)}:
                    </strong>
                    <span>
                      {displayStartDate
                        ? format(new Date(displayStartDate), DateFormat.MONTH_SHORT_DAY_SHORT_YEAR)
                        : t(($) => $.common.notAvailable)}
                    </span>

                    <strong>{t(($) => $.loopVisualization.layout.headers.fields.endDate)}:</strong>
                    <span>
                      {displayEndDate
                        ? format(new Date(displayEndDate), DateFormat.MONTH_SHORT_DAY_SHORT_YEAR)
                        : t(($) => $.common.notAvailable)}
                    </span>

                    {hasProgress && (
                      <>
                        <strong>
                          {t(($) => $.loopVisualization.layout.formatPanel.progressLabel)}:
                        </strong>
                        <span>{progress}%</span>
                      </>
                    )}
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        </Resizable>
      </div>
    );
  },
);

TimelineItem.displayName = 'TimelineItem';

export const TimelineItemContainer = memo(
  ({
    isCompact,
    canResize,
    canDragDrop,
    timelineStartDate,
    timelineEndDate,
    item,
    isExpanded,
    setExpandedChildren,
    color,
    dayWidth,
    titleSize,
    intervalStart,
    hasDependencies,
    onResizeEnd,
    onItemClick,
    row,
    itemColorById,
    containerTopOffset,
    progressFieldId,
    systemProgressMap,
  }: {
    isCompact: boolean;
    canResize: boolean;
    canDragDrop: boolean;
    timelineStartDate: string;
    timelineEndDate: string;
    item: TimelineItemType;
    isExpanded: boolean;
    setExpandedChildren: Dispatch<
      SetStateAction<{
        [key: number]: boolean;
      }>
    >;
    color: string;
    dayWidth: number;
    titleSize: TitleSize;
    intervalStart: Date;
    hasDependencies: boolean;
    onResizeEnd?: (itemId: number, newStartDate: string, newEndDate: string) => void;
    onItemClick: (itemId: number) => void;
    row?: TimelineRowType;
    itemColorById?: Record<number, string>;
    containerTopOffset: number;
    progressFieldId: number | null;
    systemProgressMap: Map<number, number> | null;
  }) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const hoverProps = useItemPositionTracking({
      itemId: item.id,
      itemRef,
      hasDependencies,
      resizeWidth: undefined,
    });

    const {
      hasAnyDate: hasAnyPlannedDate,
      start: normalizedStart,
      finish: normalizedFinish,
    } = getNormalizePlannedDates(item.plannedStartDateTime, item.plannedFinishDateTime);

    const startDateToUse =
      normalizedStart && isBefore(normalizedStart, timelineStartDate)
        ? timelineStartDate
        : (normalizedStart ?? timelineStartDate);

    const endDateToUse =
      normalizedFinish && isBefore(normalizedFinish, timelineEndDate)
        ? (normalizedFinish ?? timelineEndDate)
        : timelineEndDate;

    const numDaysInItem = differenceInDays(endDateToUse, startDateToUse) + 1;

    const calculatedWidth = Math.max(dayWidth * numDaysInItem, MIN_ITEM_WIDTH);

    const singleLaneHeight = isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;
    const itemHeight = isCompact ? ITEM_HEIGHT_COMPACT : ITEM_HEIGHT;
    const itemTopOffset = isCompact ? 2 : ITEM_LANE_HEIGHT / 2 - ITEM_HEIGHT / 2 + 4;
    const progress = systemProgressMap
      ? systemProgressToProgress(item.id, systemProgressMap)
      : progressFieldIdToProgress(progressFieldId ?? null, item);
    const progressPercentage: number | null = progress !== null ? Math.round(progress * 100) : null;

    const leftOffset = differenceInDays(startDateToUse, intervalStart) * dayWidth;
    const fontSize = getItemTitleFontSize(titleSize);

    const { stackedItems: stackedChildren, highestOrder: maxChildOrder } = useMemo(() => {
      return item.children && item.children.length > 0
        ? stackTimelineItems(item.children)
        : { stackedItems: [], highestOrder: 0 };
    }, [item.children]);

    const { bgLeft, bgRight } = useMemo(() => {
      if (!isExpanded || stackedChildren.length === 0) {
        return { bgLeft: 0, bgRight: 0 };
      }
      let minLeft = 0;
      let maxRight = 0;
      for (const child of stackedChildren) {
        const childDates = getNormalizePlannedDates(
          child.plannedStartDateTime,
          child.plannedFinishDateTime,
        );
        if (!childDates.hasAnyDate) continue;
        const childNormStart = childDates.start as string;
        const childNormFinish = childDates.finish as string;
        const childStartDate = isBefore(childNormStart, timelineStartDate)
          ? format(timelineStartDate, 'yyyy-MM-dd')
          : format(childNormStart, 'yyyy-MM-dd');
        const childEndDate = isBefore(childNormFinish, timelineEndDate)
          ? format(childNormFinish, 'yyyy-MM-dd')
          : format(timelineEndDate, 'yyyy-MM-dd');
        const childRelativeLeft =
          differenceInDays(childStartDate, intervalStart) * dayWidth - leftOffset;
        const childNumDays = differenceInDays(childEndDate, childStartDate) + 1;
        const childWidth = Math.max(dayWidth * childNumDays, MIN_ITEM_WIDTH);
        minLeft = Math.min(minLeft, childRelativeLeft);
        maxRight = Math.max(maxRight, childRelativeLeft + childWidth - calculatedWidth);
      }
      const clampedBgLeft = Math.max(minLeft, -leftOffset);
      return { bgLeft: clampedBgLeft, bgRight: -maxRight };
    }, [
      isExpanded,
      stackedChildren,
      timelineStartDate,
      timelineEndDate,
      intervalStart,
      dayWidth,
      leftOffset,
      calculatedWidth,
    ]);

    if (
      !hasAnyPlannedDate ||
      isBefore(new Date(timelineEndDate), new Date(startDateToUse)) ||
      isBefore(new Date(endDateToUse), new Date(timelineStartDate))
    ) {
      return null;
    }

    const maxContainerHeight =
      stackedChildren.length > 0 ? (1 + maxChildOrder + 1) * singleLaneHeight : singleLaneHeight;
    const containerHeight = isExpanded ? maxContainerHeight : singleLaneHeight;

    const hasChildren = item.children && item.children.length > 0;

    return (
      <div
        ref={itemRef}
        className="absolute overflow-visible z-5 transition-[height] duration-50 ease-in-out"
        style={{
          height: containerHeight,
          maxHeight: maxContainerHeight,
          minHeight: singleLaneHeight,
          left: leftOffset,
          width: calculatedWidth,
          top: containerTopOffset,
        }}
        onMouseEnter={hoverProps.onMouseEnter}
        onMouseLeave={hoverProps.onMouseLeave}
      >
        <TimelineItem
          item={item}
          color={color}
          isCompact={isCompact}
          itemTopOffset={itemTopOffset}
          calculatedWidth={calculatedWidth}
          itemHeight={itemHeight}
          progress={progressPercentage}
          fontSize={fontSize}
          timelineStartDate={timelineStartDate}
          timelineEndDate={timelineEndDate}
          startDateToUse={startDateToUse}
          endDateToUse={endDateToUse}
          isExpanded={isExpanded}
          hasChildren={hasChildren}
          onExpandToggle={(e) => {
            e.stopPropagation();
            setExpandedChildren((prev) => ({
              ...prev,
              [item.id]: !isExpanded,
            }));
          }}
          canResize={canResize && (item.isEditable ?? false)}
          canDragDrop={canDragDrop && (item.isEditable ?? false)}
          isChild={false}
          onItemClick={onItemClick}
          dayWidth={dayWidth}
          onResizeEnd={onResizeEnd}
          row={row}
        />

        {isExpanded && (
          <div className="absolute left-0 top-0 w-full">
            {/* Shaded background wrapping parent and child items */}
            <div
              className={cn('absolute z-[-1] border-l-3', {
                'rounded-lg': !isCompact,
                'rounded-sm': isCompact,
              })}
              style={{
                backgroundColor: lightenTimelineBgColor(color, 90),
                borderColor: lightenTimelineBgColor(color, 40),
                height:
                  singleLaneHeight +
                  (maxChildOrder + 1) * singleLaneHeight -
                  (isCompact ? 4 : singleLaneHeight / 2),
                top: isCompact ? 2 : 22,
                left: bgLeft,
                right: bgRight,
              }}
            />

            {stackedChildren.map((child) => {
              const childDates = getNormalizePlannedDates(
                child.plannedStartDateTime,
                child.plannedFinishDateTime,
              );
              if (!childDates.hasAnyDate) {
                return null;
              }
              const childNS = childDates.start as string;
              const childNF = childDates.finish as string;

              const childStartDate = isBefore(childNS, timelineStartDate)
                ? timelineStartDate
                : childNS;

              const childEndDate = isBefore(childNF, timelineEndDate) ? childNF : timelineEndDate;

              if (
                isBefore(new Date(timelineEndDate), new Date(childStartDate)) ||
                isBefore(new Date(childEndDate), new Date(timelineStartDate))
              ) {
                return null;
              }

              const childNumDays = differenceInDays(childEndDate, childStartDate) + 1;
              const childWidth = Math.max(dayWidth * childNumDays, MIN_ITEM_WIDTH);
              const childLeftOffset = differenceInDays(childStartDate, intervalStart) * dayWidth;
              const childTopOffset = (child.order || 0) * singleLaneHeight + itemTopOffset;
              const colorToUse = itemColorById?.[child.id] || color;
              const childColor = lightenTimelineBgColor(colorToUse, 30);
              const childCanResize = canResize && (child.isEditable ?? false);
              const childCanDragDrop = canDragDrop && (child.isEditable ?? false);
              const childRawProgress = systemProgressMap
                ? systemProgressToProgress(child.id, systemProgressMap)
                : progressFieldIdToProgress(progressFieldId ?? null, child);
              const childProgressPercentage: number | null =
                childRawProgress !== null ? Math.round(childRawProgress * 100) : null;

              return (
                <div
                  key={child.id}
                  className="absolute"
                  style={{
                    left: childLeftOffset - leftOffset,
                    width: childWidth,
                    top: singleLaneHeight,
                    height: singleLaneHeight,
                  }}
                >
                  <TimelineItem
                    item={child}
                    color={childColor}
                    isCompact={isCompact}
                    itemTopOffset={childTopOffset}
                    itemHeight={itemHeight}
                    progress={childProgressPercentage}
                    fontSize={fontSize}
                    timelineStartDate={timelineStartDate}
                    timelineEndDate={timelineEndDate}
                    startDateToUse={childStartDate}
                    endDateToUse={childEndDate}
                    canResize={childCanResize}
                    canDragDrop={childCanDragDrop}
                    isChild
                    onItemClick={onItemClick}
                    dayWidth={dayWidth}
                    calculatedWidth={childWidth}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  },
  (prev, next) =>
    prev.isCompact === next.isCompact &&
    prev.item.id === next.item.id &&
    prev.item.plannedStartDateTime === next.item.plannedStartDateTime &&
    prev.item.plannedFinishDateTime === next.item.plannedFinishDateTime &&
    prev.item.children === next.item.children &&
    prev.dayWidth === next.dayWidth &&
    prev.color === next.color &&
    prev.hasDependencies === next.hasDependencies &&
    prev.row?.id === next.row?.id &&
    prev.row?.headerId === next.row?.headerId &&
    prev.row?.parentHeaderId === next.row?.parentHeaderId &&
    prev.item.order === next.item.order &&
    prev.isExpanded === next.isExpanded &&
    prev.item.isEditable === next.item.isEditable &&
    prev.canResize === next.canResize &&
    prev.canDragDrop === next.canDragDrop &&
    prev.progressFieldId === next.progressFieldId &&
    prev.systemProgressMap === next.systemProgressMap,
);
